import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useSelector } from 'react-redux';
import strings from 'localization/strings';
import { makeStyles } from 'styles/util';
import { createBookingPriceWithDiscountInfo } from 'helpers/PriceHelper';
import { alpha } from '@material-ui/core/styles';
import rentalTypes from 'enums/rentalTypes';
import { changeValueMutator } from 'helpers/FormHelper';
import { calculateArea } from 'helpers/StorageAreaCalculator';
import { getSelectedStorageGroupId, storageGroupIsBookable, updateSelectedStorageGroupId, updateSelectedStorageGroupIdRentalType } from 'logic/bookingLogic';
import { isAfter, add } from 'date-fns';
import { getStorageGroupTitle, onlyPeriodBookingsAllowed, onlySubscriptionBookingsAllowed, getAboutOurPricesText } from 'helpers/StorageSiteHelper';
import storageGroupCategories from 'enums/storageGroupCategories';
import organizationTypes from 'enums/organizationTypes';
import { arrayDictionarify } from 'helpers/ArrayHelper';
import clsx from 'clsx';

import ImportExport from '@material-ui/icons/ImportExport';
import DateSelector from './DateSelector';
import Form from 'form/Form';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { OnChange } from 'react-final-form-listeners';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import StorageGroupSelectorPopover from './StorageGroupSelectorPopover';
import MenuItem from '@material-ui/core/MenuItem';
import { TextField} from 'mui-rff';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CustomStorageAreaFields from './CustomStorageAreaFields';
import Close from '@material-ui/icons/Close';
import Amount from 'common/Amount';

const useStyles = makeStyles(({ theme, colors, styles, fonts }) => ({
    bookingForm: {
        position: 'sticky',
        top: theme.spacing(8),
        backgroundColor: colors.secondaryColor,
        color: colors.white,
        marginTop: 'auto',
        borderRadius: `${styles.borderRadius / 1.5}px`,
        [theme.breakpoints.down('sm')]: {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
        }
    },
    priceContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(1)
    },
    priceLabel: {
        flex: '1 0 auto'
    },
    priceValue: {
        fontSize: '22px',
        lineHeight: '30.8px',
        fontFamily: fonts.medium,
        marginTop: theme.spacing(0),
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto'
    },
    discountDescription: {
        opacity: 0.8
    },
    untilFurtherDiscount: {
        textDecoration: 'line-through',
        textDecorationThickness: '1.5px'
    },
    bookingButton: {
        width: '100%',
        minHeight: '50px',
        color: colors.white,
        fontSize: theme.spacing(2),
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '150%',
        '&.Mui-disabled': {
            backgroundColor: colors.darkGrey,
            color: `${colors.textColorDisabled} !important`
        }
    },
    bookingDescription: {
        opacity: 0.8,
        marginTop: theme.spacing(2),
        zoom: 0.75,
        textAlign: 'center'
    },
    aboutOurPrices: {
        opacity: 0.8,
        marginTop: theme.spacing(2),
        zoom: 0.75,
        textAlign: 'center'
    },
    errors: {
        margin: theme.spacing(2, 0),
    },
    highlight: {
        fontWeight: 'bold'
    },
    selectedStorageGroupButton: {
        width: '100%',
        backgroundColor: colors.white,
        border: `1px solid ${colors.mediumGrey}`,
        color: colors.darkBlue,
        fontSize: theme.spacing(2),
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '160%',
        padding: theme.spacing(1.25, 2),
        margin: theme.spacing(1, 0, 2, 0),
        '& .MuiButton-label': {
            justifyContent: 'space-between',
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& .MuiSvgIcon-root': {
            fontSize: theme.spacing(2.875),
        },
        '&:hover': {
            backgroundColor: colors.whiteHover
        }
    },
    selectedStorageGroupButtonTextNotSelected: {
        color: colors.darkGrey,
    },
    textField: {
        margin: theme.spacing(1, 0, 2),
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            color: colors.darkBlue,
        },
        '& .MuiTextField-root': {

        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1.875, 1.75)
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${colors.darkBlue}`
        },
        '& .MuiSvgIcon-root': {
            color: colors.darkBlue,
            fontSize: theme.spacing(2.875)
        }
    },
    menuItem: {
        padding: theme.spacing(1, 2)
    },
    titleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(3, 4, 2, 4),
        top: theme.spacing(-0.125),
        zIndex: 1300,
        '& button': {
            color: colors.white,
            padding: 0
        },
        [theme.breakpoints.down('sm')]: {
            position: 'sticky',
        }
    },
    title: {
        color: colors.white
    },
    contentContainer: {
        padding: theme.spacing(0, 4, 3, 4),
        '& .MuiInputAdornment-root .MuiTypography-root': {
            color: colors.textColor
        }
    },
    iWantToRentContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
    },
    submitContainer: {
        zIndex: 1200,
        padding: theme.spacing(1, 0),
        bottom: 0,
        width: '100%',
        background: alpha(colors.secondaryColor, 0.5),
        position: 'sticky'
    },
    sizeContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        '& button': {
            position: 'absolute',
            padding: 0,
            right: 0,
            color: colors.white
        }
    }
}),
// branch-specific styling
({ colors }) => ({
    office: {
        bookingForm: {
            backgroundColor: colors.beige
        },
        title: {
            color: colors.textColor
        },
        contentContainer: {
            '& .MuiTypography-root': {
                color: colors.textColor
            }
        },
        submitContainer: {
            background: colors.beige ? alpha(colors.beige, 0.5) : undefined
        },
        priceValue: {
            color: colors.primaryColor
        },
        arrowButton: {
            color: colors.primaryColor
        }
    }
}));

const BookingForm = ({ storageSite, selectedCategory, initialValues, onSubmit, onValuesChange, errors = [], isPreview = false, closeModal }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const authenticationContext = useSelector(state => state.authentication.context);
    const { selectedActor } = authenticationContext;

    const [isSelectStorageOpen, setIsSelectStorageOpen ] = useState(false);
    const [isVehicleTextOpen, setIsVehicleTextOpen ] = useState(false);
    const [vehicleTextAnchorEl, setVehicleTextAnchorEl] = useState(null);
    const [storageGroupCategoryAnchorEl, setStorageGroupCategoryAnchorEl] = useState(null);

    const handleSelectStorageGroupCategoryClick = (event) => {
        handlePopoverClicked(event, setStorageGroupCategoryAnchorEl, setIsSelectStorageOpen);
    };

    const handleVehicleTextClick = (event) => {
        handlePopoverClicked(event, setVehicleTextAnchorEl, setIsVehicleTextOpen);
    };

    const handlePopoverClicked = (event, setAnchorEl, setPopoverOpen) => {
        setAnchorEl(event ? event.currentTarget : null);
        setPopoverOpen(prevState => !prevState);
    };

    return (
        <>
            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                mutators={{
                    changeValue: changeValueMutator
                }}
            >
                {
                    ({ handleSubmit, form, valid }) => {
                        const formValues = form.getState().values;

                        const selectedStorageGroupId = getSelectedStorageGroupId(formValues);

                        const selectedStorageGroup = isPreview
                            ? undefined
                            : storageSite.storageGroups.find(storageGroup => storageGroup.id === selectedStorageGroupId);

                        const maxStartDate = formValues.storageGroupCategory === storageGroupCategories.vehicle.key
                            ? undefined
                            : add(new Date(), { months: 2 });

                        const areaCalculation = selectedStorageGroup
                            ? calculateArea(
                                formValues.categories[selectedStorageGroup.category].length,
                                formValues.categories[selectedStorageGroup.category].width,
                                storageGroupCategories[selectedStorageGroup.category]
                            )
                            : undefined;

                        const subscriptionBooking = !formValues.requestedEndDate && !onlyPeriodBookingsAllowed(storageSite);

                        const booking = {
                            startDate: formValues.requestedStartDate ?? new Date(),
                            endDate: subscriptionBooking ? undefined : formValues.requestedEndDate,
                            subscriptionBooking,
                            area: areaCalculation?.adjustedArea,
                            tenantOrganizationType: selectedActor?.organizationType ?? organizationTypes.private.key
                        };

                        const amountFormatString = subscriptionBooking
                            ? strings.currencyUnitPerMonth
                            : undefined;

                        const priceInfo = selectedStorageGroup
                            ? createBookingPriceWithDiscountInfo(booking, storageSite, selectedStorageGroup, false, amountFormatString, appContext)
                            : undefined;
                        const hasPrice = priceInfo?.displayPrice > 0;

                        const handleBookingIntervalChange = () => {
                            // make sure a bookable storage group is selected
                            updateSelectedStorageGroupId(storageSite, form, undefined, appContext);
                            // make sure dates are valid
                            if(formValues.requestedStartDate && formValues.requestedEndDate && isAfter(formValues.requestedStartDate, formValues.requestedEndDate)) {
                                form.mutators.changeValue({
                                    name: 'requestedEndDate',
                                    value: add(add(formValues.requestedStartDate, { months: 1 }), { days: -1 })
                                });
                            }
                            onValuesChange(formValues);
                        };

                        const handleStorageGroupCategoryChange = selectedStorageGroupCategory => {
                            // check that StorageGroupId belongs to selected storage group category
                            form.mutators.changeValue({
                                name: 'categories',
                                value: updateSelectedStorageGroupId(storageSite, form, selectedStorageGroupCategory, appContext)
                            });
                            onValuesChange(formValues);
                        };

                        const categories = arrayDictionarify(storageSite.storageGroups, o => o.category);
                        const hasCustomAreaStorage = categories[formValues.storageGroupCategory]?.find(storageGroup => storageGroup?.rentalType === rentalTypes.customArea.key && storageGroupIsBookable(storageGroup, formValues.requestedStartDate, formValues.requestedEndDate));
                        const hasBookableStorage = categories[formValues.storageGroupCategory]?.find(storageGroup => storageGroupIsBookable(storageGroup, formValues.requestedStartDate, formValues.requestedEndDate));

                        const sendBookingRequestButtonText = storageSite.allowImmediateBookings
                            ? strings.sendBooking
                            : strings.sendBookingRequest;

                        const selectedStorageGroupText = selectedStorageGroup
                            ? getStorageGroupTitle(selectedStorageGroup, appContext, { useSpecialCustomAreaLabel: true })
                            : hasBookableStorage || isPreview ? strings.chooseSize : strings.notBookable;

                        const storageGroupCategory = storageGroupCategories[formValues.storageGroupCategory];

                        const isCustomArea = selectedStorageGroup?.rentalType === rentalTypes.customArea.key;

                        return (
                            <form onSubmit={handleSubmit} className={classes.bookingForm}>
                                <Box className={classes.titleContainer}>
                                    <Typography variant="h3" className={classes.title}>{storageGroupCategory?.rentTitle}</Typography>
                                    {
                                        closeModal &&
                                        (
                                            <IconButton onClick={closeModal}>
                                                <Close className={classes.arrowButton} />
                                            </IconButton>
                                        )
                                    }
                                </Box>

                                <Box className={classes.contentContainer}>
                                    {
                                        Object.keys(categories).length > 1 &&
                                        (
                                            <>
                                                <Box className={classes.iWantToRentContainer}>
                                                    <Typography variant="body1">{strings.iWantToRent}</Typography>
                                                    {
                                                        storageGroupCategory?.infoLinkRenderer && storageGroupCategory?.infoLinkRenderer({ onClick: handleVehicleTextClick })
                                                    }
                                                    {
                                                        storageGroupCategory?.infoTextRenderer && storageGroupCategory?.infoTextRenderer({ anchorEl: vehicleTextAnchorEl, open: isVehicleTextOpen, onClose: handleVehicleTextClick })
                                                    }
                                                </Box>

                                                <TextField
                                                    select
                                                    name="storageGroupCategory"
                                                    variant="outlined"
                                                    SelectProps={{ IconComponent: KeyboardArrowDown }}
                                                    className={classes.textField}
                                                >
                                                    {
                                                        Object.keys(categories).map(category => (
                                                            <MenuItem key={category} value={category} className={classes.menuItem}>
                                                                {storageGroupCategories[category].singleTitle}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </>
                                        )
                                    }
                                    <Box className={classes.sizeContainer}>
                                        <Typography variant="body2">{selectedStorageGroup?.rentalType === rentalTypes.customArea.key ? strings.customSize : strings.size}</Typography>
                                        {
                                            hasCustomAreaStorage && storageSite.storageGroups.length !== 1 &&
                                            (
                                                <Tooltip
                                                    title={isCustomArea ? strings.changeToFixedSizes : strings.changeToCustomSize}>
                                                    <IconButton aria-label="switch" style={{padding: 0}} onClick={() => {
                                                        form.mutators.changeValue({
                                                            name: 'categories',
                                                            value: updateSelectedStorageGroupIdRentalType(storageSite, form, !selectedStorageGroup || !isCustomArea ? rentalTypes.customArea.key : undefined, appContext)
                                                        });
                                                    }}>
                                                        <ImportExport/>
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }
                                    </Box>
                                    {
                                        // custom area text fields
                                        isCustomArea &&
                                        (
                                            <CustomStorageAreaFields
                                                storageGroup={selectedStorageGroup}
                                                value={isPreview ? 0 : selectedStorageGroup.id}
                                                checked
                                                disabled={isPreview}
                                            />
                                        )
                                    }

                                    {
                                        // storage group selector
                                        (!selectedStorageGroup || !isCustomArea) &&
                                        (
                                            <>
                                                <Button
                                                    aria-describedby="simple-popover"
                                                    className={clsx(classes.selectedStorageGroupButton, { [classes.selectedStorageGroupButtonTextNotSelected]: !selectedStorageGroup })}
                                                    type="button"
                                                    endIcon={isSelectStorageOpen ? <KeyboardArrowUp style={{ fontSize: 22 }} /> : <KeyboardArrowDown style={{ fontSize: 22 }} /> }
                                                    onClick={handleSelectStorageGroupCategoryClick}
                                                >
                                                    {selectedStorageGroupText}
                                                </Button>
                                                <StorageGroupSelectorPopover
                                                    anchorEl={storageGroupCategoryAnchorEl}
                                                    onClose={handleSelectStorageGroupCategoryClick}
                                                    open={isSelectStorageOpen}
                                                    storageSite={storageSite}
                                                    selectedCategory={selectedCategory}
                                                    form={form}
                                                    isPreview={isPreview}
                                                />
                                            </>
                                        )
                                    }

                                    <DateSelector
                                        formValues={formValues}
                                        selectedStorageGroup={selectedStorageGroup}
                                        maxStartDate={maxStartDate}
                                        endDateIsNullable={!onlyPeriodBookingsAllowed(storageSite)}
                                        endDateMustBeNull={onlySubscriptionBookingsAllowed(storageSite)}
                                        isPreview={isPreview}
                                    />

                                    {
                                        !isPreview &&
                                        (
                                            <Box className={classes.priceContainer}>
                                                <Typography className={classes.priceLabel} variant="body2">{strings.price}</Typography>
                                                <Typography variant="body2" className={classes.priceValue}>
                                                    {
                                                        hasPrice && <Amount value={priceInfo.displayPrice} currency={selectedStorageGroup.currency} formatString={amountFormatString}/>
                                                    }
                                                    {
                                                        !hasPrice && <>&ndash;</>
                                                    }
                                                </Typography>
                                                {
                                                    hasPrice && priceInfo.discountDescription && !priceInfo.untilFurtherDiscount &&
                                                    (
                                                        <Typography variant="body2" className={classes.discountDescription}>{priceInfo.discountDescription}</Typography>
                                                    )
                                                }
                                                {
                                                    hasPrice && priceInfo.untilFurtherDiscount &&
                                                    (
                                                        <Typography variant="body2" className={clsx(classes.discountDescription, classes.untilFurtherDiscount)}>
                                                            <Amount value={priceInfo.nonDiscountedPrice} currency={selectedStorageGroup.currency} formatString={amountFormatString}/>
                                                        </Typography>
                                                    )
                                                }
                                            </Box>
                                        )
                                    }
                                    {
                                        errors.length > 0 &&
                                        (
                                            <Alert severity="error" className={classes.errors}>
                                                {
                                                    errors.map((err, i) => {
                                                        return (
                                                            <Box key={i}>
                                                                {err(classes.highlight)}
                                                            </Box>
                                                        );
                                                    })
                                                }
                                            </Alert>
                                        )
                                    }

                                    <Box className={classes.submitContainer}>
                                        <Button
                                            type="submit"
                                            color="primary"
                                            variant="contained"
                                            className={classes.bookingButton}
                                            disabled={!valid || isPreview || !selectedStorageGroupId}
                                        >
                                            {sendBookingRequestButtonText}
                                        </Button>
                                    </Box>

                                    {
                                        !storageSite.allowImmediateBookings &&
                                        (
                                            <Typography variant="body1" className={classes.bookingDescription}>
                                                {strings.bookingButtonDescription}
                                            </Typography>
                                        )
                                    }
                                    <Typography variant="body1" className={classes.aboutOurPrices}>
                                        {getAboutOurPricesText(storageSite, selectedCategory, selectedStorageGroup, selectedActor?.organizationType, appContext)}
                                    </Typography>
                                </Box>

                                <OnChange name="storageGroupCategory">{handleStorageGroupCategoryChange}</OnChange>
                                <OnChange name="requestedStartDate">{handleBookingIntervalChange}</OnChange>
                                <OnChange name="requestedEndDate">{handleBookingIntervalChange}</OnChange>
                            </form>
                        );
                    }
                }
            </Form>
        </>
    );
};

BookingForm.propTypes = {
    storageSite: PropTypes.object.isRequired,
    selectedCategory: PropTypes.object,
    initialValues: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onValuesChange: PropTypes.func.isRequired,
    errors: PropTypes.array,
    isPreview: PropTypes.bool,
    closeModal: PropTypes.func,
};

export default BookingForm;
